import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Loader2, Copy } from 'lucide-react';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { Button } from '@/components/Button';
import { useTelegram } from '@/hooks/useTelegram';
import { fmtNum, mealTypeLabel } from '@/utils/format';
import { copyMealToToday } from '@/hooks/useFoodHistory';
import { DayStrip } from '@/components/DayStrip';
import { consumedAtFor, dayPhrase } from '@/lib/mealDay';
import type { MealType } from '@/types';
import type { HistoryMeal } from '@/hooks/useFoodHistory';

interface CopyMealModalProps {
  open: boolean;
  onClose: () => void;
  meal: HistoryMeal | null;
  onCopied?: () => void;
}

function getDefaultMealType(): MealType {
  const h = new Date().getHours();
  if (h < 11) return 'breakfast';
  if (h < 16) return 'lunch';
  if (h < 21) return 'dinner';
  return 'snack';
}

/**
 * Модалка выбора куда копировать приём из истории.
 * Юзер выбирает:
 *  1. Тип приёма (завтрак / обед / ужин / перекус)
 *  2. День — любой из недели назад или до двух дней вперёд
 * 
 * По умолчанию: текущий тип приёма по времени + сегодня.
 */
export function CopyMealModal({ open, onClose, meal, onCopied }: CopyMealModalProps) {
  const { haptic } = useTelegram();
  const [mealType, setMealType] = useState<MealType>(getDefaultMealType());
  const [dayOffset, setDayOffset] = useState(0);
  const [copying, setCopying] = useState(false);

  // Сброс при открытии нового приёма
  useEffect(() => {
    if (open && meal) {
      setMealType(getDefaultMealType());
      setDayOffset(0);
    }
  }, [open, meal]);

  if (!meal) return null;

  // Информация о исходном приёме
  const sourceDate = new Date(meal.consumed_at);
  const sourceDayLabel = meal.days_ago === 0 ? 'Сегодня' 
                      : meal.days_ago === 1 ? 'Вчера' 
                      : format(sourceDate, 'd MMM', { locale: ru });
  const sourceMealType = mealTypeLabel[meal.meal_type as MealType] ?? meal.meal_type;

  async function handleCopy() {
    if (!meal) return;
    
    setCopying(true);
    haptic('medium');

    try {
      // День уходит в саму функцию копирования: раньше дата правилась вторым запросом,
      // и запись на завтра успевала засчитаться в серию сегодня.
      const result = await copyMealToToday(meal.meal_id, mealType, consumedAtFor(dayOffset, mealType));

      if (!result.success || !result.meal_id) {
        haptic('error');
        alert(`Не удалось скопировать: ${result.error ?? 'ошибка'}`);
        return;
      }

      haptic('success');
      onCopied?.();
      onClose();
    } catch (e: any) {
      console.error(e);
      haptic('error');
      alert(`Ошибка: ${e.message}`);
    } finally {
      setCopying(false);
    }
  }

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', stiffness: 350, damping: 30 }}
            className="fixed bottom-16 left-0 right-0 z-50 bg-bg rounded-t-3xl flex flex-col"
            style={{
              maxHeight: 'calc(100vh - 144px)',
              paddingBottom: 'env(safe-area-inset-bottom)',
            }}
          >
            {/* СКРОЛЛИРУЕМЫЙ КОНТЕНТ */}
            <div className="flex-1 overflow-y-auto px-5 pt-5 pb-4">
              <div className="max-w-md mx-auto">
                {/* Drag handle */}
                <div className="w-12 h-1 bg-line rounded-full mx-auto mb-4" />

                {/* Header */}
                <div className="flex items-start gap-3 mb-5">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-glow/20 to-sky-glow/15 flex items-center justify-center shrink-0">
                    <Copy size={20} className="text-emerald-glow" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] font-bold text-emerald-glow uppercase tracking-[1.5px] mb-0.5">
                      Копировать приём
                    </div>
                    <h2 className="font-display text-lg font-bold text-ink truncate">
                      {meal.preview_text}
                    </h2>
                    <p className="text-[11px] text-ink-dim mt-0.5">
                      Из: {sourceDayLabel} · {sourceMealType} · {meal.total_calories} ккал
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={onClose}
                    className="w-9 h-9 rounded-xl bg-bg-card border border-line flex items-center justify-center text-ink-muted"
                  >
                    <X size={18} />
                  </button>
                </div>

                {/* Выбор типа приёма */}
                <div className="mb-4">
                  <label className="label mb-2 block">Куда добавить?</label>
                  <div className="grid grid-cols-2 gap-1.5">
                    {(['breakfast', 'lunch', 'dinner', 'snack'] as MealType[]).map((mt) => (
                      <button
                        type="button"
                        key={mt}
                        onClick={() => {
                          setMealType(mt);
                          haptic('light');
                        }}
                        className={`py-2.5 rounded-lg text-xs font-medium transition-colors ${
                          mealType === mt
                            ? 'bg-emerald-glow text-bg'
                            : 'bg-bg-card text-ink-muted border border-line'
                        }`}
                      >
                        {mealTypeLabel[mt]}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Выбор дня */}
                <div className="mb-5">
                  <label className="label mb-2 block">{dayOffset > 0 ? 'На какой день' : 'Когда'}</label>
                  <DayStrip value={dayOffset} onChange={setDayOffset} bleed />
                </div>

                {/* Превью */}
                <div className="card-elevated p-4 border-emerald-glow/30">
                  <div className="text-[10px] text-ink-dim uppercase tracking-[0.8px] font-medium mb-2">
                    Будет записано
                  </div>
                  <div className="font-display text-lg font-bold text-emerald-glow mb-1">
                    <span className="first-letter:uppercase inline-block">{dayPhrase(dayOffset) || 'сегодня'}</span> · {mealTypeLabel[mealType]}
                  </div>
                  <div className="text-sm text-ink mb-2">{meal.preview_text}</div>
                  <div className="flex gap-3 text-xs">
                    <span className="text-ink font-semibold">{meal.total_calories} ккал</span>
                    <span className="text-emerald-glow">Б {fmtNum(meal.total_protein_g, 1)}г</span>
                    <span className="text-warn">Ж {fmtNum(meal.total_fat_g, 1)}г</span>
                    <span className="text-sky-glow">У {fmtNum(meal.total_carbs_g, 1)}г</span>
                  </div>
                </div>
              </div>
            </div>

            {/* STICKY кнопка */}
            <div className="shrink-0 px-5 pt-3 pb-5 border-t border-line/30 bg-bg">
              <div className="max-w-md mx-auto">
                <Button
                  fullWidth
                  size="lg"
                  disabled={copying}
                  onClick={handleCopy}
                  icon={copying ? <Loader2 size={18} className="animate-spin" /> : <Copy size={18} />}
                >
                  {copying ? 'Копируем…' : `Скопировать в ${mealTypeLabel[mealType].toLowerCase()}`}
                </Button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
